import { Suspense, useState } from "react";
import { Box, Flex } from "@chakra-ui/react";

import {
  getReservationIntervalList,
  getReservationIntervalListKey,
} from "modules/appointment/api/getReservationIntervalList";
import { IEReservationIntervalElement } from "modules/appointment/types/IEReservationIntervalElement";

import { DateObject, DateObjectType } from "utils/DateObject";
import { useGetFriendlyUrl } from "utils/hooks/useGetFriendlyUrl";

import { Spinner } from "components/Spinner";
import { ChakraText } from "components/ChakraText";
import { useReactQuery } from "components/RemoteData";
import { ErrorBoundary } from "components/ErrorBoundary/ErrorBoundary";

import { ReservationCalendar } from "../ReservationCalendar";
import { AvailableHours } from "../AvailableHours";
import { IChildrenAsFunction } from "types/IChildrenAsFunction";

interface IProps {
  availableDate: DateObjectType;
  queueId: string;
  banBookingToday?: boolean;
  children: IChildrenAsFunction<{
    selectedInterval: IEReservationIntervalElement | null;
    clearInterval: () => void;
  }>;
}

const ThrowableTimeTable = ({ availableDate, queueId, children, banBookingToday}: IProps) => {
  const { friendlyUrl } = useGetFriendlyUrl();
  const [date, setDate] = useState<DateObjectType | null>(availableDate);
  const [
    selectedInterval,
    setSelectedInterval,
  ] = useState<IEReservationIntervalElement | null>(null);
  //month jest pełną datą wymaganą przez Calendar kiedy używamy handlerów na zmianę dnia i miesiąca
  const [month, setMonth] = useState<DateObjectType | null>(date);

  const {
    data: reservationIntervalList,
  } = useReactQuery(
    getReservationIntervalListKey(month?.getMonth(), month?.getFullYear()),
    () => getReservationIntervalList(queueId, month, friendlyUrl)
  );

  if (!reservationIntervalList) {
    return <></>;
  }

  const handleSetDate = (date: DateObjectType | null) => {
    setDate(date);
    setSelectedInterval(null);
  };

  const selectedDateTime = reservationIntervalList.find((group) => {
    return date?.baseFormat() === group.date.baseFormat();
  });

  return (
    <Flex direction={{ base: "column", lg: "row" }} mt={8}>
      <Box
        width={{
          base: "100%",
          lg: "40%",
        }}
      >
        <ReservationCalendar
          reservationIntervalList={reservationIntervalList}
          date={date}
          setDate={handleSetDate}
          setMonth={setMonth}
        />
      </Box>
      <Box
        width={{
          base: "100%",
          lg: "60%",
        }}
        px={8}
      >   
        {(banBookingToday && 
        selectedDateTime?.date.getDayOfMonth() === DateObject().getDayOfMonth() &&
        selectedDateTime?.date.getMonth() === DateObject().getMonth() &&
        selectedDateTime?.date.getFullYear() === DateObject().getFullYear())? (
        <ChakraText fontSize="2xl" textAlign="center">
           Brak możliwości rezerwacji terminu w dniu bieżącym
          </ChakraText>
        ) :(
        date ? (
          <>
            <ChakraText fontSize="2xl" textAlign="center" mb={2}>
              Dostępne godziny
            </ChakraText>
            <AvailableHours
              selectedDay={selectedDateTime}
              selectedInterval={selectedInterval}
              setInterval={setSelectedInterval}
            />
            <Box mt={8} textAlign="center" h="150px">
              <ChakraText fontSize="2xl" mb={2}>
                Wybrany termin:
              </ChakraText>
              <ChakraText
                fontSize="2xl"
                textDecoration="underline"
                fontWeight="bold"
              >
                {date.userFormat()}
              </ChakraText>
              <ChakraText
                fontSize="2xl"
                textDecoration="underline"
                fontWeight="bold"
              >
                {selectedInterval && (
                  <>godz. {selectedInterval?.startDateTime.getTime()}</>
                )}
              </ChakraText>
            </Box>
            <Box mt={8} textAlign="center">
              {children({
                selectedInterval: selectedInterval,
                clearInterval: () => setSelectedInterval(null),
              })}
            </Box>
          </>
        ) : (
          <ChakraText fontSize="2xl" textAlign="center">
            Wybierz dzień a następnie godzinę
          </ChakraText>
        ))}
      </Box>
    </Flex>
  );
};

const TimeTable = ({ ...props }: IProps) => {
  return (
    <ErrorBoundary>
      <Suspense fallback={<Spinner mt={4} />}>
        <ThrowableTimeTable {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};

export { TimeTable };
